import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebouncedMemo } from '@hooks/debounceAsync';
import { CommonConstant } from '@utility/constants/common';
import CatalogService from '@utility/services/catalog';
import { catalogMappingKeys } from '../lib/query-keys';
import type { ConflictItem, ConflictListingsParams } from '../lib/types';
import { ALL } from '../lib/unmatched-params';

const noItems: ConflictItem[] = [];

type ConflictFilter = {
  page: number;
  limit: number;
  search: string;
  regionId: string;
  /** Marketplace types, by name; none means every one. */
  marketplaces: string[];
};

/* Catalog items with attributes still missing, to be filled in. */
export function useConflictListings(initialSearch = '') {
  const [filter, setFilter] = useState<ConflictFilter>({
    page: 1,
    limit: CommonConstant.defaultPageSize,
    search: initialSearch,
    regionId: ALL,
    marketplaces: [],
  });

  const params: ConflictListingsParams = {
    page: filter.page,
    limit: filter.limit,
    search: filter.search.trim(),
    ...(filter.regionId === ALL
      ? {}
      : { marketplace_region_id: filter.regionId }),
    ...(filter.marketplaces.length
      ? { marketplaceTypes: filter.marketplaces }
      : {}),
  };
  /* Typing in the search box asks once it settles, not on every key. */
  const applied = useDebouncedMemo<ConflictListingsParams>(
    () => params,
    [JSON.stringify(params)],
    500,
  );

  const listQuery = useQuery({
    queryKey: catalogMappingKeys.conflicts(applied ?? params),
    queryFn: async (): Promise<ConflictItem[]> =>
      (await CatalogService.conflictAttributesItems(applied)).data ?? [],
    enabled: !!applied,
    placeholderData: keepPreviousData,
  });

  const pageQuery = useQuery({
    queryKey: catalogMappingKeys.conflictPages(applied ?? params),
    queryFn: (): Promise<{ total_data?: number }> =>
      CatalogService.conflictAttributesPageData(applied),
    enabled: !!applied,
    placeholderData: keepPreviousData,
  });

  /* Any filter but the page starts the list over. */
  const filterChanged = <K extends keyof ConflictFilter>(
    key: K,
    value: ConflictFilter[K],
  ) => {
    setFilter(previous => ({
      ...previous,
      [key]: value,
      page: key === 'page' ? (value as number) : 1,
    }));
  };

  return {
    data: listQuery.data ?? noItems,
    loading: !applied || listQuery.isPending,
    totalData: pageQuery.data?.total_data ?? 0,
    filter,
    filterChanged,
    refetch: () => {
      listQuery.refetch();
      pageQuery.refetch();
    },
  };
}
